import cn from 'classnames';
import * as React from 'react';

import { hasDisplayName } from 'utils/hasDisplayName';

import { SpacingRowBase, SpacingRowBaseProps } from '../SpacingRowBase';
import { SpacingUnit } from '../config';
import { usePassThroughSpacing, SpacingCtx } from '../context';

import styles from './SpacingRow.module.scss';
import SpacingRowItem from './SpacingRowItem';

export enum SpacingRowJustify {
  start = 'start',
  spaceBetween = 'space-between',
  center = 'center',
  end = 'end',
}

export enum SpacingRowWrap {
  wrap = 'wrap',
  nowrap = 'nowrap',
}

export enum SpacingRowAlign {
  normal = 'normal',
  stretch = 'stretch',
  center = 'center',
  end = 'end',
}

export type SpacingRowProps = SpacingRowBaseProps & {
  hSpacing?: SpacingUnit;
  vSpacing?: SpacingUnit;
  justify?: SpacingRowJustify;
  align?: SpacingRowAlign;
  rowWrap?: SpacingRowWrap;
};

const SpacingRow: React.FC<SpacingRowProps> = ({
  justify = SpacingRowJustify.start,
  align = SpacingRowAlign.normal,
  children,
  hSpacing = SpacingUnit.unit2,
  vSpacing = SpacingUnit.none,
  rowWrap = SpacingRowWrap.nowrap,
  ...p
}: SpacingRowProps) => {
  const filteredChildren = React.useMemo(
    () => React.Children.toArray(children).filter(Boolean),
    [children]
  );

  const nextSpacing = usePassThroughSpacing({
    hSpacing,
    vSpacing,
  });

  return (
    <SpacingCtx.Provider value={nextSpacing}>
      <SpacingRowBase
        {...p}
        className={cn(
          p.className,
          styles['row'],
          styles[`row_justify-${justify}`],
          styles[`row_align-${align}`],
          styles[`row_wrap-${rowWrap}`],
          styles[`row_spacing-v-${nextSpacing.vSpacing}`],
          styles[`row_spacing-h-${nextSpacing.hSpacing}`]
        )}
      >
        {React.Children.map(filteredChildren, (child) =>
          hasDisplayName(child) &&
          child.type.displayName === SpacingRowItem.displayName ? (
            child
          ) : (
            <SpacingRowItem>{child}</SpacingRowItem>
          )
        )}
      </SpacingRowBase>
    </SpacingCtx.Provider>
  );
};

SpacingRow.displayName = 'Spacing.Row';

export default SpacingRow;
